import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ClipboardList } from "lucide-react";
import { z } from "zod";
import {
  SORT_DIRECTIONS,
  businessDate,
  fromYmd,
  hours,
  isoWeek,
  periodRange,
  weekRange,
  type MyTimesheetRow,
  type SortDirection,
} from "@contractorhub/shared";
import { myTimesheetsQuery } from "../../../lib/queries";
import { isHalfWeek } from "../../../components/timesheet/period";
import { cellState, type WeekState } from "../../../lib/week-state";
import {
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  StatusBadge,
  buttonClass,
  type Column,
} from "../../../components/ui";

/**
 * Only a direction, and deliberately no `sort` field: there is one sortable column,
 * so naming which one would describe a choice that does not exist. It lives in the
 * URL rather than in state for the same reason the admin queue's does — a sorted
 * list survives a refresh and can be sent to someone.
 */
const searchSchema = z.object({
  dir: z.enum(SORT_DIRECTIONS).default("desc"),
});

export const Route = createFileRoute("/_authed/_contractor/my-submissions")({
  validateSearch: searchSchema,
  // No `loaderDeps`: the sort is applied to rows this page derives, so it changes
  // nothing about what has to be fetched.
  loader: ({ context }) => context.queryClient.ensureQueryData(myTimesheetsQuery({})),
  component: MySubmissionsPage,
});

const STAMP = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

/**
 * One week, rolled up across however many projects carried it.
 *
 * The table used to be one row per *timesheet* — that is, per project per week —
 * grouped under a week header. So a contractor splitting their time across three
 * engagements read three rows, three status badges and three "Edit" links for
 * what is, to them, one week they either filed or did not. The projects were the
 * table's subject and the week was a heading above it; this inverts that.
 */
type WeekSummary = {
  weekStart: string;
  /**
   * The row's period (TIME-1). A week straddling a month end is two rows here,
   * not one: its halves are filed, decided and invoiced apart, so rolling them
   * into one status would hide which month is still open.
   */
  periodStart: string;
  periodEnd: string;
  monday: Date;
  totalHours: number;
  state: WeekState;
  /** The whole week's, so the earliest filing and the latest decision. */
  submittedAt: string | null;
  reviewedAt: string | null;
  /** Whether anything in the week is still the contractor's to change. */
  editable: boolean;
};

/**
 * Sorting happens here, on the rolled-up weeks, rather than in the API's `ORDER BY`.
 *
 * Not for want of a server. The table's subject is one row per *week* — hours,
 * earliest filing and latest decision all summed across however many projects
 * carried it — and those rows exist only in this function; the endpoint returns one
 * row per project per week and has no week to order. `listMyTimesheets` is also
 * unpaginated (DATA-7), so the client already holds every week the contractor has
 * ever filed: there is no page a server sort could reach that this one cannot.
 */
function summarise(
  rows: MyTimesheetRow[],
  today: Date,
  dir: SortDirection
): WeekSummary[] {
  const byPeriod = new Map<string, MyTimesheetRow[]>();
  for (const row of rows) {
    const list = byPeriod.get(row.periodStart) ?? [];
    list.push(row);
    byPeriod.set(row.periodStart, list);
  }

  return [...byPeriod.values()]
    .map((weekRows) => {
      const { weekStart, periodStart, periodEnd } = weekRows[0]!;
      const monday = fromYmd(weekStart);
      const stamps = (pick: (row: MyTimesheetRow) => string | null) =>
        weekRows.map(pick).filter((value) => value !== null);
      const submitted = stamps((row) => row.submittedAt);
      const reviewed = stamps((row) => row.reviewedAt);

      return {
        weekStart,
        periodStart,
        periodEnd,
        monday,
        totalHours: weekRows.reduce((sum, row) => sum + row.totalHours, 0),
        /**
         * `engaged: true` by construction — a row exists because a timesheet was
         * saved, and `saveTimesheet` refuses a week outside the engagement. There
         * is no unengaged week in this list to mislabel.
         */
        state: cellState(weekRows, true, monday, today),
        // Earliest filing, latest decision: the week is not filed until its last
        // project is, and not decided until its last project is.
        submittedAt: submitted.length > 0 ? submitted.sort()[0]! : null,
        reviewedAt: reviewed.length > 0 ? reviewed.sort().at(-1)! : null,
        editable: weekRows.some(
          (row) => row.status === "DRAFT" || row.status === "CHANGES_REQUESTED"
        ),
      };
    })
    // `periodStart` is a YYYY-MM-DD string and the group key, so this compare is
    // both chronological and unique — no tiebreaker to invent.
    .sort((a, b) =>
      dir === "asc"
        ? a.periodStart.localeCompare(b.periodStart)
        : b.periodStart.localeCompare(a.periodStart)
    );
}

const columns: Column<WeekSummary>[] = [
  {
    key: "week",
    header: "Week",
    // The only sortable column. The rest are per-week rollups a contractor has no
    // reason to order by — they scan this list for a week, not for an outlier.
    sortable: true,
    cell: (row) => (
      <div className="min-w-0">
        <div className="font-medium text-text">Week {isoWeek(row.monday)}</div>
        <div className="text-xs text-subtle">
          {isHalfWeek(row)
            ? periodRange(fromYmd(row.periodStart), fromYmd(row.periodEnd))
            : weekRange(row.monday)}
        </div>
      </div>
    ),
  },
  {
    key: "hours",
    header: "Hours",
    align: "right",
    cell: (row) => hours(row.totalHours),
    cellClassName: "font-medium tabular-nums",
  },
  {
    key: "status",
    header: "Status",
    cell: (row) => <StatusBadge status={row.state} />,
  },
  {
    key: "submitted",
    header: "Submitted",
    priority: "md",
    cell: (row) =>
      row.submittedAt ? (
        STAMP.format(new Date(row.submittedAt))
      ) : (
        <span className="text-subtle">—</span>
      ),
    cellClassName: "text-muted",
  },
  {
    key: "reviewed",
    header: "Decided",
    priority: "md",
    cell: (row) =>
      row.reviewedAt ? (
        STAMP.format(new Date(row.reviewedAt))
      ) : (
        <span className="text-subtle">—</span>
      ),
    cellClassName: "text-muted",
  },
  {
    key: "actions",
    header: "",
    align: "right",
    cell: (row) => (
      <Link
        to="/my-timesheet"
        // Week only. The projects are no longer this table's subject, so there is
        // no project to deep-link to — the editor opens the whole week.
        search={{ week: row.weekStart }}
        className="text-sm font-medium text-accent hover:underline"
      >
        {row.editable ? "Edit" : "View"}
      </Link>
    ),
  },
];

function MySubmissionsPage() {
  const { dir } = Route.useSearch();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(myTimesheetsQuery({}));
  const today = businessDate();
  const weeks = summarise(data.timesheets, today, dir);

  return (
    <div>
      <PageHeader
        title="Submissions"
        description="Every week you've saved or submitted, and where it stands."
      />
      <Card className="overflow-hidden">
        <DataTable
          columns={columns}
          rows={weeks}
          getRowKey={(row) => row.periodStart}
          caption="Your timesheet submissions, by week"
          // `week` is always the active column, so `DataTable`'s own flip is the
          // right one here — no need for the queue's third-click-resets rule, which
          // exists to escape a column you did not mean to sort by.
          sort={{ key: "week", direction: dir }}
          onSortChange={(next) =>
            void navigate({
              to: "/my-submissions",
              search: { dir: next.direction },
            })
          }
          empty={
            <EmptyState
              icon={ClipboardList}
              title="No timesheets yet"
              description="Once you save or submit a week it will show up here."
              action={
                <Link
                  to="/my-timesheet"
                  className={buttonClass({ variant: "secondary" })}
                >
                  Fill in this week&apos;s hours
                </Link>
              }
            />
          }
        />
      </Card>
    </div>
  );
}
